import { Save as SaveIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateWebhook, useWebhook } from '@/components/features/webhooks/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { EventSelector } from '@/components/webhooks/event-selector';

function EditWebhookPage() {
  const { t } = useTranslation('integrations');
  const navigate = useNavigate();
  // Access the parent route's id parameter
  const { id } = Route.useParams() as { id: string };
  const { data: webhook, isLoading } = useWebhook(id ?? '');
  const updateWebhook = useUpdateWebhook();

  // Initialize form from webhook data
  useEffect(() => {
    if (webhook) {
      const retryPolicy =
        typeof webhook.retryPolicy === 'string'
          ? JSON.parse(webhook.retryPolicy)
          : webhook.retryPolicy;
      const events =
        typeof webhook.events === 'string' ? JSON.parse(webhook.events) : webhook.events;

      setFormData({
        name: webhook.name,
        url: webhook.url,
        events,
        maxAttempts: retryPolicy.maxAttempts,
        initialDelay: retryPolicy.initialDelay,
        multiplier: retryPolicy.multiplier,
        retentionDays: webhook.retentionDays,
        isActive: webhook.isActive === 1,
      });
    }
  }, [webhook]);

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [] as string[],
    maxAttempts: 3,
    initialDelay: 60,
    multiplier: 2,
    retentionDays: 7,
    isActive: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    updateWebhook.mutate(
      {
        id: id ?? '',
        data: {
          name: formData.name,
          url: formData.url,
          events: formData.events,
          retryPolicy: {
            maxAttempts: formData.maxAttempts,
            initialDelay: formData.initialDelay,
            multiplier: formData.multiplier,
          },
          retentionDays: formData.retentionDays,
          isActive: formData.isActive,
        },
      },
      {
        onSuccess: () => {
          navigate({ to: '/integrations/webhooks' });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!webhook) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-muted-foreground">Webhook not found</p>
          <Link to="/integrations/webhooks">
            <Button variant="outline" className="mt-4">
              Back to Webhooks
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <div className="flex items-center gap-4">
        <Link to="/integrations/webhooks">
          <Button variant="ghost" size="icon">
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('webhooks.edit')}</h1>
          <p className="text-sm text-muted-foreground">Update webhook configuration</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">{t('webhooks.form.name')}</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="url">{t('webhooks.form.url')}</Label>
          <Input
            id="url"
            type="url"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label>{t('webhooks.form.events')}</Label>
          <EventSelector
            value={formData.events}
            onChange={(events) => setFormData({ ...formData, events })}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>{t('webhooks.form.maxAttempts')}</Label>
            <Input
              type="number"
              min="1"
              max="10"
              value={formData.maxAttempts}
              onChange={(e) =>
                setFormData({ ...formData, maxAttempts: parseInt(e.target.value, 10) })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>{t('webhooks.form.initialDelay')}</Label>
            <Input
              type="number"
              min="1"
              value={formData.initialDelay}
              onChange={(e) =>
                setFormData({ ...formData, initialDelay: parseInt(e.target.value, 10) })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>{t('webhooks.form.backoffMultiplier')}</Label>
            <Input
              type="number"
              min="1"
              step="0.1"
              value={formData.multiplier}
              onChange={(e) => setFormData({ ...formData, multiplier: parseFloat(e.target.value) })}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="retentionDays">{t('webhooks.form.retentionDays')}</Label>
          <Input
            id="retentionDays"
            type="number"
            min="1"
            max="90"
            value={formData.retentionDays}
            onChange={(e) =>
              setFormData({ ...formData, retentionDays: parseInt(e.target.value, 10) })
            }
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="isActive">{t('webhooks.form.isActive')}</Label>
          <Switch
            id="isActive"
            checked={formData.isActive}
            onCheckedChange={(checked) => setFormData({ ...formData, isActive: !!checked })}
          />
        </div>

        <div className="flex gap-2">
          <Button type="submit" disabled={updateWebhook.isPending}>
            <HugeiconsIcon icon={SaveIcon} size={16} className="mr-2" />
            {updateWebhook.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
          <Link to="/integrations/webhooks">
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/integrations/webhooks/$id/edit')({
  component: EditWebhookPage,
});
